import { createSignal, For, Show } from 'solid-js';
import {
  state, openItem, newFile, deleteFile, duplicateAsFile, showModal, pushToast, importFiles, collab, leaveRoom,
} from '../state';
import type { LibraryFile, LibraryRoom } from '../types';

interface CtxState {
  kind: 'file' | 'room';
  id: string;
  x: number;
  y: number;
}

export function Sidebar() {
  const [ctx, setCtx] = createSignal<CtxState | null>(null);

  const openCtx = (kind: 'file' | 'room', id: string, e: MouseEvent) => {
    e.stopPropagation();
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setCtx({ kind, id, x: r.left, y: r.bottom + 4 });
  };

  const closeCtx = () => setCtx(null);

  const fmtTime = (ts: number): string => {
    const d = Date.now() - ts;
    if (d < 60_000) return 'just now';
    if (d < 3_600_000) return `${Math.floor(d / 60_000)}m ago`;
    if (d < 86_400_000) return `${Math.floor(d / 3_600_000)}h ago`;
    return `${Math.floor(d / 86_400_000)}d ago`;
  };
  const fmtSize = (n: number): string => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <aside class="sidebar" onClick={closeCtx}>
      <div class="sb-header">
        <span class="brand">md</span>
      </div>
      <div class="sb-scroll">
        <div class="sb-section">
          <div class="sb-head">
            <span>My files</span>
            <div class="sb-head-actions">
              <button onClick={() => void importFiles()} title="Import .md files from disk">Import</button>
              <button onClick={() => void newFile()}>+ New</button>
            </div>
          </div>
          <ul class="sb-list">
            <For each={state.files}>
              {(f: LibraryFile) => (
                <li
                  class={`sb-item${state.openKind === 'file' && state.openId === f.id ? ' active' : ''}`}
                  onClick={() => void openItem('file', f.id)}
                >
                  <span class="sb-icon">▭</span>
                  <div class="sb-meta">
                    <div class="sb-title">{f.title}</div>
                    <div class="sb-sub">{fmtSize(f.size)} · {fmtTime(f.updatedAt)}</div>
                  </div>
                  <button class="sb-more" onClick={(e) => openCtx('file', f.id, e)}>⋯</button>
                </li>
              )}
            </For>
            <Show when={state.files.length === 0}>
              <li class="sb-empty">No files yet — click <em>+ New</em>.</li>
            </Show>
          </ul>
        </div>
        <div class="sb-section">
          <div class="sb-head">
            <span>Rooms</span>
            <button onClick={() => showModal('join')}>+ Join…</button>
          </div>
          <ul class="sb-list">
            <For each={state.rooms}>
              {(r: LibraryRoom) => (
                <li
                  class={`sb-item room${state.openKind === 'room' && state.openId === r.id ? ' active' : ''}`}
                  onClick={() => void openItem('room', r.id)}
                >
                  <span class="sb-icon">⌬</span>
                  <div class="sb-meta">
                    <div class="sb-title">{r.title}</div>
                    <div class="sb-sub">
                      room: <code>{r.roomId}</code>
                      <Show when={r.unread}><span class="sb-pulse" title="new activity" /></Show>
                    </div>
                  </div>
                  <button class="sb-more" onClick={(e) => openCtx('room', r.id, e)}>⋯</button>
                </li>
              )}
            </For>
            <Show when={state.rooms.length === 0}>
              <li class="sb-empty">No rooms — share a file or join via link.</li>
            </Show>
          </ul>
        </div>
      </div>

      <Show when={ctx()}>
        {(c) => (
          <div
            class="ctx-popover"
            style={{ left: `${c().x}px`, top: `${c().y}px` }}
            onClick={(e) => e.stopPropagation()}
          >
            <Show when={c().kind === 'file'}>
              <button onClick={() => { void openItem('file', c().id); closeCtx(); }}>Open</button>
              <button onClick={() => { showModal('rename', c().id); closeCtx(); }}>Rename…</button>
              <button onClick={() => { showModal('share', c().id); closeCtx(); }}>Share as room…</button>
              <div class="sep" />
              <button class="danger" onClick={() => { void deleteFile(c().id); closeCtx(); pushToast({ severity: 'success', title: 'File removed' }); }}>Delete</button>
            </Show>
            <Show when={c().kind === 'room'}>
              <button onClick={() => { void openItem('room', c().id); closeCtx(); }}>Open</button>
              <button onClick={() => {
                const r = state.rooms.find((x) => x.id === c().id);
                if (r) {
                  navigator.clipboard?.writeText(`${location.origin}/#room=${r.roomId}`).catch(() => {});
                  pushToast({ severity: 'success', title: 'Link copied' });
                }
                closeCtx();
              }}>Copy share link</button>
              <button onClick={() => { void duplicateAsFile(c().id); closeCtx(); }}>Duplicate as local file</button>
              <div class="sep" />
              <button class="danger" onClick={() => {
                const id = c().id;
                closeCtx();
                // Only warn if the currently-open room has local edits worth keeping.
                // Yjs UndoManager only tracks the local user's edits, which is exactly
                // what we want as the "dirty" signal.
                const ctx = collab();
                const dirty =
                  state.openId === id &&
                  state.openKind === 'room' &&
                  !!ctx && ctx.undoManager.undoStack.length > 0;
                if (dirty) {
                  showModal('leave-room', id);
                } else {
                  void leaveRoom(id);
                  pushToast({ severity: 'info', title: 'Left the room', body: 'Rejoin anytime via the share link.' });
                }
              }}>Leave room</button>
            </Show>
          </div>
        )}
      </Show>
    </aside>
  );
}
